<template>
    <div class="row mx-0 top-container">
        <div class="col-md-12 main-content">
            <!-- Needed to force data reactivity when this seller has a counter successfully placed-->
            <span class="hidden">{{hasPlacedCounter}}</span>

            <!-- Title -->
            <div class="row title-row">
                <div class="col-lg-4 col-sm-12 col-12 credit">
                    Payment Terms: 
                    <span v-if="fuelGroupSelected.negotiationList[activeTabIndex - 1].buyer">
                        {{fuelGroupSelected.negotiationList[activeTabIndex - 1].buyer.credit.days | creditdaysformat}}
                    </span>
                    <span v-else>
                        N/A
                    </span>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 user text-center">
                    My Counter:
                </div>
                <div class="col-lg-4 col-sm-6 col-6 counterpart text-center">
                    Buyer's Negotiation
                </div>
            </div>

            <!-- Price -->
            <div class="row info-row">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Price (ppl)
                </div>
                <div class="col-lg-4 col-sm-6 col-6 price-box user px-1">
                    <div v-if="!counteringActive" class="details-price">
                        <!-- Display the placed counter's price if there is one or leave it empty -->
                        <span v-if="!hasPlacedCounter" class="inactive-field">
                            {{counterOffer.price | priceformat}}
                        </span>
                        <span v-else :class="{'text-danger': Number(placedCounter.price).toFixed(2) !== Number(originalNegotiationPrice).toFixed(2)}">
                           {{placedCounter.price | priceformat}}
                        </span>
                    </div>
                    <div v-else>
                        <cleave class="input-price" v-model="counterOffer.price" placeholder=""
                            :options="numeralPositive" @blur.native="setPriceFormat(counterOffer.price)"
                            @click.native="clearErrorMessages()"></cleave>
                        <span v-if="errorShown == 'PRICE'" class="text-danger error-message-popup">{{errorMessagePrice}}</span>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 price-box counterpart px-1 flex-column">
                    <span class="details-price" style="margin-bottom: -10px;"
                        :class="{'text-danger': placedCounter !== '' && (Number(placedCounter.price).toFixed(2) !== originalNegotiationPrice.toFixed(2))}">
                        {{originalNegotiationPrice | priceformat}}
                    </span>
                    <div v-if="hasPlacedCounter" class="price-delta"
                        :class="(placedCounter.price - originalNegotiationPrice) !== 0 ? 'higher' : null">
                        {{originalNegotiationPrice - placedCounter.price | pricedeltaformat}}
                    </div>
                </div>
            </div>

            <!-- Time Remaining -->
            <div v-if="!finalStatusDisplayed" class="row info-row">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Time Remaining
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <div v-if="!counteringActive">
                        <!-- Display countdown whilst offer is still active, otherwise display its status -->
                        <div v-if="hasPlacedCounter">
                            <span v-if="!(new Date() > new Date(placedCounter.expires)) &&
                                !(wasMyCounterAccepted[activeTabIndex - 1]) && fuelGroupSelected.negotiationList[activeTabIndex - 1].status_id == 'ACTIVE'" 
                                class="negotiation-countdown"
                                :class="{'timer-displaying' : !$breakpoint.smAndDown}"
                                >
                                <span class="text-display">{{(new Date(placedCounter.expires) - new Date())/1000 | countdownshortformat}}</span>
                                <radial-progress-bar class="countdown-progress" :diameter="35" 
                                    :totalSteps="parseInt((new Date(placedCounter.expires) - new Date(placedCounter.created))/1000)" 
                                    :completedSteps="(new Date(placedCounter.expires) - new Date())/1000" />
                            </span>
                            <span v-else class="negotiation-countdown counter text-center">
                                <span v-if="wasMyCounterAccepted[activeTabIndex - 1]" class="text-primary">Counter sold</span>
                                <span v-else class="text-danger">Your counter offer has expired</span>
                            </span>
                        </div>
                    </div>
                    <div v-else class="select-wrapper">
                        <select v-model="counterOffer.period">
                            <option :value="offerPeriod" v-for="(offerPeriod,index) in offerPeriodList" :key="index">
                                {{offerPeriod | negotiationperiodformat}}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <span v-if="getNegotiationListActiveStatus[activeTabIndex - 1] && 
                        fuelGroupSelected.negotiationList[activeTabIndex - 1].status_id == 'ACTIVE'" 
                        class="negotiation-countdown timer-displaying">
                        <span class="text-display">{{getNegotiationListTimer[activeTabIndex - 1] | countdownshortformat}}</span>
                        <radial-progress-bar class="countdown-progress" :diameter="35" 
                            :totalSteps="parseInt((new Date(fuelGroupSelected.negotiationList[activeTabIndex - 1].expires) - new Date(fuelGroupSelected.negotiationList[activeTabIndex - 1].created))/1000)" 
                            :completedSteps="getNegotiationListTimer[activeTabIndex - 1]" />
                    </span>
                    <span v-else class="negotiation-countdown text-center">
                        <span v-if="(fuelGroupSelected.negotiationList[activeTabIndex - 1].status_id == 'COMPLETED' &&
                            fuelGroupSelected.negotiationList[activeTabIndex - 1].accepting_user && 
                            fuelGroupSelected.negotiationList[activeTabIndex - 1].accepting_user.account.id == userLoggedIn.id) ||
                            wasMyCounterAccepted[activeTabIndex - 1]"
                            class="text-primary">
                            <span v-if="!wasMyCounterAccepted[activeTabIndex - 1]">Sold</span>
                        </span>
                        <span v-else class="text-danger error-message-popup">Expired, cancelled or accepted</span>
                    </span>
                </div>
            </div>

            <!-- Volume -->
            <div class="row info-row">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Volume/L
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <div v-if="!counteringActive">
                        <!-- Display the placed counter's volume if there is one or leave it empty initially -->
                        <span v-if="!hasPlacedCounter" class="inactive-field">
                            {{counterOffer.quantity | volumeformat}}
                        </span>
                        <span v-else 
                            :class="{'text-danger':Number(placedCounter.quantity) !== Number(fuelGroupSelected.negotiationList[activeTabIndex - 1].quantity)}">
                            {{placedCounter.quantity | volumeformat}}
                        </span>
                    </div>
                    <div v-else>
                        <cleave v-model="counterOffer.quantity" 
                            :options="numeralPositive" @click.native="clearErrorMessages()"></cleave>
                        <span v-if="errorShown == 'QUANTITY'" class="text-danger error-message-popup">{{errorMessageQuantity}}</span>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <span :class="{'text-danger': placedCounter !== '' && (Number(placedCounter.quantity) !== Number(fuelGroupSelected.negotiationList[activeTabIndex - 1].quantity))}">
                        {{fuelGroupSelected.negotiationList[activeTabIndex - 1].quantity | volumeformat}}
                    </span>
                </div>
            </div>

            <!-- Terminal -->
            <div v-if="fuelGroupSelected.fuel.type == 'SPOT'" class="row info-row">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Terminal
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <div v-if="!counteringActive">
                        <!-- Display the placed counter's terminal if there is one or leave it as 'None selected' initially -->
                        <span v-if="!hasPlacedCounter" class="inactive-field">
                            {{counterOffer.terminal.name}}
                        </span>
                        <div v-else>
                            <span v-if="placedCounter.terminal"
                                :class="{'text-danger':placedCounter.terminal._id !== fuelGroupSelected.negotiationList[activeTabIndex - 1].terminal_id}">
                                {{placedCounter.terminal.name}}
                            </span>
                            <span v-else>
                                N/A
                            </span>
                        </div>
                    </div>
                    <div v-else class="select-wrapper">
                        <select id="select-terminal" v-model="counterOffer.terminal"
                            :class="{'text-danger':counterOffer.terminal._id !== fuelGroupSelected.negotiationList[activeTabIndex - 1].terminal_id}">
                            <option :value="terminal" v-for="(terminal,index) in terminals" :key="index">
                                {{terminal.name}}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <span v-if="fuelGroupSelected.negotiationList[activeTabIndex - 1].terminal" 
                        :class="{'text-danger': placedCounter !== '' && placedCounter.terminal && 
                        (placedCounter.terminal._id !== fuelGroupSelected.negotiationList[activeTabIndex - 1].terminal._id)}">
                        {{fuelGroupSelected.negotiationList[activeTabIndex - 1].terminal.name}}
                    </span>
                    <span v-else>N/A</span>
                </div>
            </div>

            <!-- Measurement -->
            <div class="row info-row">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Measurement
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <div v-if="!counteringActive">
                        <!-- Display the placed counter's measurement if there is one or leave it as 'N/A' initially -->
                        <span v-if="!hasPlacedCounter" class="inactive-field">
                            {{counterOffer.fuel_measurement | fuelmeasurementtype}}
                        </span>
                        <span v-else  
                            :class="{'text-danger':placedCounter.fuel_measurement_id !== fuelGroupSelected.negotiationList[activeTabIndex - 1].fuel_measurement_id}">
                            {{placedCounter.fuel_measurement_id | fuelmeasurementtype}}
                        </span>
                    </div>
                    <div v-else class="select-wrapper">
                        <select id="select-measurement" v-model="counterOffer.fuel_measurement"
                            :class="{'text-danger':counterOffer.fuel_measurement !== fuelGroupSelected.negotiationList[activeTabIndex - 1].fuel_measurement_id}">
                            <option :value="measurementType" v-for="(measurementType,index) in measurements" :key="index">
                                {{measurementType | fuelmeasurementtype}}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <span :class="{'text-danger': placedCounter !== '' && (placedCounter.fuel_measurement_id !== fuelGroupSelected.negotiationList[activeTabIndex - 1].fuel_measurement_id)}">
                        {{fuelGroupSelected.negotiationList[activeTabIndex - 1].fuel_measurement_id | fuelmeasurementtype}}
                    </span>
                </div>
            </div>

            <!-- Lifting Period -->
            <div v-if="fuelGroupSelected.fuel.type !== 'BUNKERING'" class="row info-row">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Lifting Period
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <div v-if="!counteringActive">
                        <!-- Display the placed counter's Lifting Period if there is one or leave it empty initially -->
                        <span v-if="!hasPlacedCounter" class="inactive-field">
                            {{counterOffer.collectionTime | collectiondaysformat}}
                        </span>
                        <span v-else :class="{'text-danger':placedCounter.collectionTime !== fuelGroupSelected.negotiationList[activeTabIndex - 1].collectionTime}">
                            {{placedCounter.collectionTime | collectiondaysformat}}
                        </span>
                    </div>
                    <div v-else class="select-wrapper">
                        <select id="select-collection-period" v-model="counterOffer.collectionTime"
                            :class="{'text-danger':counterOffer.collectionTime !== fuelGroupSelected.negotiationList[activeTabIndex - 1].collectionTime}">
                            <option :value="collectionPeriod" v-for="(collectionPeriod,index) in collectionPeriodList" :key="index">
                                {{collectionPeriod | collectiondaysformat}}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <span :class="{'text-danger': placedCounter !== '' && (placedCounter.collectionTime !== fuelGroupSelected.negotiationList[activeTabIndex - 1].collectionTime)}">
                        {{fuelGroupSelected.negotiationList[activeTabIndex - 1].collectionTime | collectiondaysformat}}
                    </span>
                </div>
            </div>

            <!-- Final status -->
            <div v-if="finalStatusDisplayed" class="row info-row status">
                <div class="col-lg-4 col-sm-12 col-12">
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <span v-if="wasMyCounterAccepted[activeTabIndex - 1]" class="positive">
                        <i class="fas fa-check-circle"></i>&nbsp;Counter sold
                    </span>
                    <span v-else-if="hasPlacedCounter" class="text-danger">Expired</span>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1 text-center">
                    <span v-if="(fuelGroupSelected.negotiationList[activeTabIndex - 1].status_id == 'COMPLETED' &&
                        fuelGroupSelected.negotiationList[activeTabIndex - 1].accepting_user && 
                        fuelGroupSelected.negotiationList[activeTabIndex - 1].accepting_user.account.id == userLoggedIn.id)"
                        class="positive">
                        <i class="fas fa-check-circle"></i>&nbsp;Sold
                    </span>
                    <span v-else-if="!wasMyCounterAccepted[activeTabIndex - 1]" class="text-danger">Expired, cancelled or accepted</span>
                </div>
            </div>

            <!-- Total Cost -->
            <div class="row info-row total">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Total Cost
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <div v-if="!hasPlacedCounter">
                        <span :class="[{'text-danger': counteringActive && counterTotalCost.toFixed(2) !== originalTotalCost.toFixed(2)},
                            {'inactive-field': !counteringActive}]">
                            £{{counterTotalCost | priceformat}}
                        </span>
                    </div>
                    <div v-else>
                        <span v-if="!counteringActive" :class="{'text-danger': (placedCounter.quantity * placedCounter.price/100).toFixed(2) !== originalTotalCost.toFixed(2)}">
                            £{{placedCounter.quantity * placedCounter.price/100 | priceformat}}
                        </span>
                        <span v-else :class="{'text-danger': counterTotalCost.toFixed(2) !== originalTotalCost.toFixed(2)}">
                            £{{counterTotalCost | priceformat}}
                        </span>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <span :class="{'text-danger': placedCounter !== '' && ((placedCounter.price/100 * placedCounter.quantity).toFixed(2) !== originalTotalCost.toFixed(2))}">
                        £{{originalTotalCost | priceformat}}
                    </span>
                </div>
            </div>

            <!-- Credit Line Selection -->
            <div v-if="displayCreditLineRow" class="row info-row" :class="{'mb-4': counteringActive}">
                <div class="col-lg-4 col-sm-12 col-12 label">
                    Credit Line
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <div v-if="!counteringActive">
                        <span v-if="!hasPlacedCounter" class="inactive-field">&mdash;</span>
                        <!-- Display the selected brand -->
                        <span v-else-if="placedCounter.brand.name">{{ placedCounter.brand.name }}</span>
                    </div>
                    <div v-else class="select-wrapper">
                        <select id="select-credit-line" v-model="counterOffer.brand">
                            <option :value="brand" v-for="(brand, index) in brandSelection" :key="index" :disabled="!brand">
                                <span v-if="brand">{{ brand.name }}</span>
                                <span v-else>Select a credit line</span>
                            </option>
                        </select>
                        <span v-if="errorShown == 'CREDIT_LINE'" class="text-danger error-message-popup mt-4">{{ errorMessageCreditLine }}</span>
                    </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <!-- Display the selected brand -->
                    <span v-if="fuelGroupSelected.negotiationList[activeTabIndex - 1].status_id == 'COMPLETED' && fuelGroupSelected.negotiationList[activeTabIndex - 1].brand">
                        {{ fuelGroupSelected.negotiationList[activeTabIndex - 1].brand.name }}
                    </span>
                    <span v-else class="inactive-field">&mdash;</span>
                </div>
            </div>

            <!-- Actions -->
            <div class="row info-row actions">
                <div class="col-lg-4 col-sm-12 col-12">
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <!-- Display "Renegotiate" button as long as original negotiation is still active -->
                    <button class="btn btn-secondary w-100" v-if="!counteringActive"
                        :disabled="counteringActive || !getNegotiationListActiveStatus[activeTabIndex - 1] || fuelGroupSelected.negotiationList[activeTabIndex - 1].status_id !== 'ACTIVE' || isLoadingData" 
                        @click="renegotiate()">Renegotiate</button>
                    <button class="btn btn-primary w-100" v-else :disabled="!counteringActive" @click="placeCounterOffer($event)">Confirm</button>
                </div>
                <div class="col-lg-4 col-sm-6 col-6 px-1">
                    <button class="btn btn-primary w-100" v-if="!counteringActive" :disabled="!getNegotiationListActiveStatus[activeTabIndex - 1] || 
                        fuelGroupSelected.negotiationList[activeTabIndex - 1].status_id !== 'ACTIVE'"
                        @click="acceptNegotiation(fuelGroupSelected.negotiationList[activeTabIndex - 1]._id)">Accept</button>
                </div>
            </div>

            <!-- SPINNER TO INDICATE THAT WE ARE LOADING THE DATA -->
            <div v-if="isLoadingData" class="spinner-container">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import breakpoint from '@/mixins/index';

export default {
    mixins: [breakpoint],
    data(){
        return {
            counterOffer: {
                quantity: 0,
                terminal: {
                    _id: 0,
                    name: 'None selected'
                },
                fuel_measurement: 0,
                price: '',
                collectionTime: 0,
                period: 0,
                brand: null
            },
            counteringActive: false,
            measurements: ['STANDARD', 'AMBIENT'],
            collectionPeriodList: [0, 1, 2, 3, 7, 30],
            offerPeriodList: [5, 4, 3],
            countdownFunction: '',
            errorMessagePrice: 'Offer price must be greater than 0',
            errorMessageQuantity: 'Volume must be an integer greater than 0',
            errorMessageCreditLine: 'Please select a credit line',
            errorShown: '',
            creditLineSelectionRequired: false,
            isLoadingData: false
        }
    },
    computed: {
        ...mapState('negotiation',['fuelGroupSelected','activeTabIndex', 'showAcceptNegotiation', 'placedCounter']),
        ...mapState('inputCheckers',['numeralPositive']),
        ...mapState('login',['userLoggedIn']),
        ...mapState('terminals',['terminals']),
        ...mapState('notifications', ['notificationList']),
        ...mapGetters('negotiation',['getFuelGroupChanges', 'getNegotiationListTimer', 'getNegotiationListActiveStatus', 
            'wasMyCounterAccepted', 'hasPlacedCounter']),

        brandSelection: function(){
            let brands = [];
            const accountDetails = this.$store.getters['accounts/accountDetails'];
            
            if (accountDetails && accountDetails.brands && accountDetails.brands.length > 0){
                brands = [null, ...accountDetails.brands];
                this.creditLineSelectionRequired = true;
            } else {
                this.creditLineSelectionRequired = false;
            }

            return brands;
        },
        /* IF the current negotiation ID is found in the current notification list, mark as viewed */
        clearCurrentNegotiationNotification: function(){
            this.shouldNotificationBeRemoved()
        },
        originalNegotiationPrice: {
            get: function(){
                let price = 0

                if (this.activeTabIndex && this.fuelGroupSelected.negotiationList){
                    if (this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].status_id === 'COMPLETED')
                        price = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].accepted_counter_offer_id ? 
                            this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].priceBeforeOrder : this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].price
                    else
                        price = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].type_id == 'FIXED' ? 
                            this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].price : this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].livePrice
                }

                return price
            }
        },
        originalTotalCost: {
            get: function(){
                let total = 0

                if (this.originalNegotiationPrice){
                    total = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].quantity * this.originalNegotiationPrice/100
                }

                return total
            }
        },
        counterTotalCost: {
            get: function(){
                return this.counterOffer.price/100 * this.counterOffer.quantity
            }
        },
        finalStatusDisplayed: {
            get: function(){
                if (this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].status_id !== 'ACTIVE' || 
                    (!this.getNegotiationListActiveStatus[this.activeTabIndex - 1] && !(!(new Date() > new Date(this.placedCounter.expires)) && !(this.wasMyCounterAccepted[this.activeTabIndex - 1]) && this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].status_id == 'ACTIVE')) ||
                    (!this.getNegotiationListActiveStatus[this.activeTabIndex - 1] && !this.hasPlacedCounter)){
                    return true
                }
                else {
                    return false
                }
            }
        },
        openAcceptNegotiationModal: function(){
            if (this.showAcceptNegotiation){
                let element = this.$root.$refs.acceptNegotiation.$el
                $(element).modal({backdrop: 'static', keyboard: false})
            }
        },
        turnOffEditingOnSwitchActiveTabIndex: function(){
            if (this.activeTabIndex){
                this.resetCounterFields()
            }
        },
        fetchConversation: function(){
            if (this.placedCounter){
                const conversationToFetchId = this.placedCounter.conversation_id
                this.loadConversation(conversationToFetchId)
            }
            else {
                this.deselectConversation()
            }
        },
        displayCreditLineRow: function(){
            const currentNegotiation = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1];

            return (this.hasPlacedCounter && this.placedCounter.brand) // The seller has placed a counter with a credit line selected
                || (currentNegotiation.status_id == 'COMPLETED' && currentNegotiation.brand) // The seller has accepted this negotiation with a credit line selected
                || (currentNegotiation.status_id !== 'COMPLETED' && this.creditLineSelectionRequired); // Negotiation has not been accepted and seller needs to respond by selecting a credit line
        }
    },
    watch: {
        brandSelection(){},
        openAcceptNegotiationModal(){},
        turnOffEditingOnSwitchActiveTabIndex(){},
        fetchConversation(){},
        clearCurrentNegotiationNotification(){}, // Watcher for clearing current negotiation off notification list
    },
    methods: {
        ...mapActions('conversation', ['loadConversation', 'deselectConversation']),
        ...mapActions('negotiation', ['updateNegotiationListTimer', 'addCounterOffer', 'clearPlacedCounter',
            'updateShowAcceptNegotiation', 'editCounterOffer', 'checkNegotiationAvailability', 'showNegotiationUnavailableWindow']),
        ...mapActions('notifications', ['updateNotification']),
        setPriceFormat: function(price){
            this.counterOffer.price = Number(price).toFixed(2)
        },
        clearErrorMessages: function(){
            this.errorShown = ''
        },
        shouldNotificationBeRemoved: function(){
            /* Remove red dot notifications for this negotiation */
            let currentRelevantNotification = this.notificationList.find(notification => notification.type_id === 'NEGOTIATION' && notification.group_id === this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1]._id && notification.viewed === null)

            if(currentRelevantNotification){
                this.updateNotification(currentRelevantNotification.group_id);
            }
        },
        renegotiate: function(){
            this.counteringActive = true

            /* Check if there are notifications related to this negotiation */
            this.shouldNotificationBeRemoved()

            /* If this is revising a counter, then populate all fields with the counter's details */
            if (this.hasPlacedCounter){
                this.counterOffer.price = this.placedCounter.price.toFixed(2)
                this.counterOffer.quantity = this.placedCounter.quantity
                this.counterOffer.terminal = this.placedCounter.terminal
                this.counterOffer.fuel_measurement = this.placedCounter.fuel_measurement_id
                this.counterOffer.collectionTime = this.placedCounter.collectionTime
                this.counterOffer.period = this.placedCounter.counteroffer_period_id / 60
                this.counterOffer.brand = JSON.parse(JSON.stringify(this.placedCounter.brand));
            }
            else {
                this.counterOffer.price = ''
                this.counterOffer.quantity = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].quantity
                this.counterOffer.terminal = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].terminal
                this.counterOffer.fuel_measurement = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].fuel_measurement_id
                this.counterOffer.collectionTime = this.fuelGroupSelected.negotiationList[this.activeTabIndex - 1].collectionTime
                this.counterOffer.period = 5
                this.counterOffer.brand = null;
            }
        },
        placeCounterOffer: async function(event){
            this.isLoadingData = true;

            /* Check if there are notifications related to this negotiation */
            this.shouldNotificationBeRemoved()

            const negotiationId = this.getFuelGroupChanges.negotiationList[this.activeTabIndex - 1]._id
            const negotiationAvailable = await this.checkNegotiationAvailability({negotiationId: negotiationId, isPlacingCounter: true})

            /* Check whether negotiation is still active or not */
            if (this.hasPlacedCounter || (!this.hasPlacedCounter && negotiationAvailable)){
                const moment = require('moment')

                if (this.counterOffer.price == 0){
                    this.isLoadingData = false;
                    this.errorShown = 'PRICE'
                    setTimeout(()=>{
                        this.errorShown = ''
                    }, 2000);
                    return;
                }
                else if (this.counterOffer.quantity == 0 || !Number.isInteger(Number(this.counterOffer.quantity))){
                    this.isLoadingData = false;
                    this.errorShown = 'QUANTITY'
                    setTimeout(()=>{
                        this.errorShown = ''
                    }, 2000)
                    return;
                }
                else if (this.creditLineSelectionRequired && !this.counterOffer.brand){
                    this.isLoadingData = false;
                    this.errorShown = 'CREDIT_LINE'
                    setTimeout(()=>{
                        this.errorShown = ''
                    }, 2000);
                    return;
                }
                else {
                    const counter = {
                        userId: this.userLoggedIn.userId,
                        price: Number(this.counterOffer.price),
                        quantity: Number(this.counterOffer.quantity),
                        fuel_measurement_id: this.counterOffer.fuel_measurement,
                        collectionTime: this.counterOffer.collectionTime,
                        terminal_id: this.fuelGroupSelected.fuel.type == 'SPOT' ?
                            this.counterOffer.terminal._id : null,
                        periodId: this.counterOffer.period + 'M'
                    }

                    if (this.creditLineSelectionRequired) counter.brand = this.counterOffer.brand;

                    const counterOfferId = this.placedCounter._id

                    const counterInfo = {
                        counter: counter,
                        negotiation_id: negotiationId,
                        counterOfferId: counterOfferId
                    }

                    /* Decide whether this is revising or placing a new counter offer */
                    if (this.hasPlacedCounter){
                        await this.editCounterOffer(counterInfo)
                    }
                    else {
                        await this.addCounterOffer(counterInfo)
                    }

                    /* Reset all counter offer values */
                    this.resetCounterFields()
                }
            }
            else {
                this.showNegotiationUnavailableWindow(true)
            }
            
            setTimeout(() => {
                this.isLoadingData = false;
            }, 2000);
        },
        acceptNegotiation: async function(negotiationId){
            /* Check if there are notifications related to this negotiation */
            this.shouldNotificationBeRemoved()

            const negotiationAvailable = await this.checkNegotiationAvailability({negotiationId: negotiationId})

            /* Check whether negotiation is still active or not */
            if (negotiationAvailable){
                this.updateShowAcceptNegotiation(true)
            }
            else {
                this.showNegotiationUnavailableWindow(true)
            }
        },
        resetCounterFields: function(){
            this.counterOffer.quantity = 0
            this.counterOffer.terminal = {
                _id: 0,
                name: 'None selected'
            }
            this.counterOffer.fuel_measurement = 0
            this.counterOffer.price = ''
            this.counterOffer.collectionTime = 0
            this.counterOffer.period = 0
            this.counterOffer.brand = null;
            
            this.counteringActive = false
        }
    },
    beforeDestroy() {
        clearInterval(this.countdownFunction)
        this.clearPlacedCounter()
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pages/_negotiations.scss";
@import "@/assets/scss/components/_spinner.scss";
</style>
